import { QueryClient } from '@tanstack/react-query';

const CACHE_KEY = 'TANSTACK_QUERY_CACHE';
const CACHE_VERSION = 'v2';

/**
 * Simple localStorage-based cache persister for TanStack Query
 * Saves and restores query cache to survive page refreshes
 */
export function createPersistedQueryClient() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        cacheTime: 6 * 60 * 60 * 1000, // 6 hours
      },
    },
  });

  // Restore cache from localStorage on initialization
  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (cachedData) {
      const { version, timestamp, state } = JSON.parse(cachedData);
      
      // Check if cache is still valid (not older than 6 hours)
      const now = Date.now();
      const maxAge = 6 * 60 * 60 * 1000; // 6 hours
      
      if (version === CACHE_VERSION && (now - timestamp) < maxAge) {
        // Restore queries to cache
        if (state && state.queries) {
          state.queries.forEach((query: any) => {
            queryClient.setQueryData(query.queryKey, query.state.data);
          });
        }
        console.log('[Cache] Restored from localStorage');
      } else {
        console.log('[Cache] Expired or version mismatch, clearing');
        localStorage.removeItem(CACHE_KEY);
      }
    }
  } catch (error) {
    console.error('[Cache] Failed to restore from localStorage:', error);
    localStorage.removeItem(CACHE_KEY);
  }

  // Save cache to localStorage periodically
  const saveCache = () => {
    try {
      const cache = queryClient.getQueryCache();
      const queries = cache.getAll().map(query => ({
        queryKey: query.queryKey,
        state: {
          data: query.state.data,
          dataUpdatedAt: query.state.dataUpdatedAt,
        },
      }));

      const cacheData = {
        version: CACHE_VERSION,
        timestamp: Date.now(),
        state: { queries },
      };

      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      console.log('[Cache] Saved to localStorage');
    } catch (error) {
      console.error('[Cache] Failed to save to localStorage:', error);
    }
  };

  // Save cache when queries are updated
  queryClient.getQueryCache().subscribe(() => {
    // Debounce saves to avoid excessive writes
    if (typeof window !== 'undefined') {
      clearTimeout((window as any).__cacheTimeout);
      (window as any).__cacheTimeout = setTimeout(saveCache, 1000);
    }
  });

  // Save cache before page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', saveCache);
  }

  return queryClient;
}
